// API Constants - Centralized API routes and endpoints

// ============================================
// API BASE ROUTES
// ============================================

export const API_ROUTES = {
  MANAGER_V1: '/managerv1',
  MANAGER_REGISTER: import.meta.env.VITE_MANAGER_ROUTE || 'headOfficeAuthV143',
}

// ============================================
// API VERSIONS / RESOURCE PATHS
// ============================================

export const API_VERSIONS = {
  DASHBOARD: 'overviewV1',
  DOCLINK_DATA: 'doclinkV1',
  QUDA_DATA: 'qudaV1',
  ADMIN_V1: 'phAdminV1',
  /** ChemLink (multi-chemist routing) */
  CHEM_LINK_V1: 'chemLinkV1',
  /** PH Admin overview dashboards: phAdminV1/adminOverviewV1/… */
  ADMIN_OVERVIEW_V1: 'adminOverviewV1',
  /** DocLink admin routes live under phAdminV1/DocLinkAdminV1/ */
  DOCLINK_ADMIN_V1: 'DocLinkAdminV1',
  /** HubMed admin reporting & onboarding: phAdminV1/HubMedAdminV1/ */
  HUBMED_ADMIN_V1: 'HubMedAdminV1',
  /** Hola admin: phAdminV1/HolaAdminV1/ */
  HOLA_ADMIN_V1: 'HolaAdminV1',
  /** Medmate admin: phAdminV1/MedmateAdminV1/ */
  MEDMATE_ADMIN_V1: 'MedmateAdminV1',
  /** HaloText admin: phAdminV1/HaloTextAdminV1/ */
  HALOTEXT_ADMIN_V1: 'HaloTextAdminV1',
  /** Zephyr admin: phAdminV1/ZephyrAdminV1/ */
  ZEPHYR_ADMIN_V1: 'ZephyrAdminV1',
  /** Cherub AI admin: phAdminV1/CherubAdminV1/ */
  CHERUB_ADMIN_V1: 'CherubAdminV1',
  /** Workflow CRM: phAdminV1/WorkflowAdminV1/ */
  WORKFLOW_ADMIN_V1: 'WorkflowAdminV1',
  /** Conferences: phAdminV1/ConferencesAdminV1/ */
  CONFERENCES_ADMIN_V1: 'ConferencesAdminV1',
}

/** Manager overview endpoints (no PHAdmin required — filtered by ManagerChemistLink). */
export const MANAGER_OVERVIEW_ENDPOINTS = {
  OVERVIEW: 'ManagerOverview',
  QUDA: 'ManagerQuda',
  DOCLINK: 'ManagerDocLink',
  HALOPAY: 'ManagerHaloPay',
  HALOTEXT: 'ManagerHaloText',
  MEDIDROP: 'ManagerMediDrop',
  CHEMIST_PAGE: 'ManagerChemistPage',
  CHEMIST_QUDA: 'ManagerChemistQuda',
  CHEMIST_HALOPAY: 'ManagerChemistHaloPay',
  CHEMIST_HALOTEXT: 'ManagerChemistHaloText',
  CHEMIST_DOCLINK: 'ManagerChemistDocLink',
  CHEMIST_MEDIDROP: 'ManagerChemistMediDrop',
}

/** phAdminV1/adminOverviewV1/{endpoint} (manager-scoped variants) */
export const buildManagerOverviewPath = (endpoint) =>
  `${API_ROUTES.MANAGER_V1}/${API_VERSIONS.ADMIN_V1}/${API_VERSIONS.ADMIN_OVERVIEW_V1}/${endpoint}`

// ============================================
// DASHBOARD ENDPOINTS
// ============================================

export const DASHBOARD_ENDPOINTS = {
  MAIN_DATA: 'ChemistData_All',
  CHEMIST_DATA: 'ChemistData_Single', // Append /{chemistID}
  CHEMIST_SUBS: 'ChemistSubs',
  PING_APP_TEAM: 'PingAppTeam',
  IS_PH_ADMIN: 'AdminAuthority',
}

// ============================================
// CHART DATA ENDPOINTS
// ============================================

export const CHART_ENDPOINTS = {
  DOCLINK_DASHBOARD: 'ChemistData_All',
}

export const QUDA_ENDPOINTS = {
  QUDA_DASHBOARD: 'ChemistData_All',
  ITEM_ORDERS: 'ItemOrders', // Append /PHUUID/ItemName/DateStart/DateEnd
  SUPPLIER_ORDERS: 'SupplierOrders',
  SUPPLIER_PHARMACIES: 'SupplierPharmacies',
}

// ============================================
// ADMIN ENDPOINTS
// ============================================

export const ADMIN_ENDPOINTS = {
  SETUP_HALOPAY: 'SetupHaloPay',
  CREATE_XERO_CONTACT: 'CreateXeroContact',
  CHEMIST_LIST: 'ChemistList',
  ERROR_LOG: 'ErrorLog',
  DEBUG_LOG: 'DebugLog',
  SEARCH_HALAXY_FOR_CHEMIST: 'SearchHalaxyForChemist',
  LINK_HUBMED_CLINIC: 'LinkHubmedClinic',
  HUBMED_DOCLINK_PRICING: 'HubmedDoclinkPricing',
  LEADERBOARD_COMPETITIONS: 'LeaderboardCompetitions',
  LEADERBOARD: 'Leaderboard',
  STRIPE_PORTAL_LINK: 'StripePortalLink',
}

/** PH Admin sandbox routes under adminOverviewV1 (see API docs). */
export const ADMIN_OVERVIEW_ENDPOINTS = {
  OVERVIEW: 'AdminOverview',
  CHEMIST_DETAIL: 'AdminChemistDetail',
  CHEMIST_PAGE: 'ChemistPage',
  QUDA: 'AdminQudaOverview',
  DOCLINK: 'AdminDocLinkOverview',
  DOCLINK_APPOINTMENTS: 'AdminDocLinkAppointments',
  SMS_BILLING: 'AdminSmsBilling',
  HALOPAY: 'AdminHaloPayOverview',
  HALOTEXT: 'AdminHaloTextOverview',
  REPORTS: 'AdminReports',
  CHEMIST_QUDA: 'ChemistQuda',
  CHEMIST_HALOPAY: 'ChemistHaloPay',
  CHEMIST_HALOTEXT: 'ChemistHaloText',
  CHEMIST_DOCLINK: 'ChemistDocLink',
  CHEMIST_MEDIDROP: 'ChemistMediDrop',
  CHEMIST_WEBBIT: 'ChemistWebbit',
  MEDIDROP: 'AdminMediDropOverview',
  WEBBIT: 'AdminWebbitOverview',
  TRIAL_HEALTH: 'AdminTrialHealth',
  TERMINAL_ANALYTICS: 'TerminalAnalytics',
  CHEMIST_TERMINALS: 'ChemistTerminals',
  ENGAGEMENT_REPORT: 'EngagementReport',
}

// ============================================
// AUTH ENDPOINTS
// ============================================

export const AUTH_ENDPOINTS = {
  MANAGER_LOGIN: 'ManagerLogin',
  VALIDATE_MANAGER: 'ValidateManager',
  LOGIN_WITH_GOOGLE: 'LoginWithGoogle',
  CREATE_USER_WITH_GOOGLE: 'CreateUserWithGoogle',
  LINK_GOOGLE_ACCOUNT: 'LinkGoogleAccount',
  CHECK_USER: 'CheckUser',
}

// ============================================
// HELPER FUNCTIONS TO BUILD FULL PATHS
// ============================================

/**
 * Build a dashboard API path
 * @param {string} endpoint - The dashboard endpoint (e.g., DASHBOARD_ENDPOINTS.MAIN_DATA)
 * @param {string|number} [id] - Optional ID to append to path
 * @returns {string} Full API path
 */
export const buildDashboardPath = (endpoint, id = null) => {
  const base = `${API_ROUTES.MANAGER_V1}/${API_VERSIONS.DASHBOARD}/${endpoint}`
  return id ? `${base}/${id}` : base
}

/**
 * Build a chart data API path
 * @param {string} endpoint - The chart endpoint (e.g., CHART_ENDPOINTS.DOCLINK_DASHBOARD)
 * @returns {string} Full API path
 */
export const buildChartPath = (endpoint) => {
  return `${API_ROUTES.MANAGER_V1}/${API_VERSIONS.DOCLINK_DATA}/${endpoint}`
}

export const buildQudaPath = (endpoint) => {
  return `${API_ROUTES.MANAGER_V1}/${API_VERSIONS.QUDA_DATA}/${endpoint}`
}

export const buildAdminPath = (endpoint) => {
  return `${API_ROUTES.MANAGER_V1}/${API_VERSIONS.ADMIN_V1}/${endpoint}`
}

/** phAdminV1/adminOverviewV1/{endpoint} */
export const buildAdminOverviewPath = (endpoint) =>
  `${API_ROUTES.MANAGER_V1}/${API_VERSIONS.ADMIN_V1}/${API_VERSIONS.ADMIN_OVERVIEW_V1}/${endpoint}`

/** GET …/GroupManagers/:managerId/Chemists */
export const buildGroupManagerLinkedChemistsPath = (managerId) =>
  buildAdminPath(`GroupManagers/${managerId}/Chemists`)

/**
 * phAdminV1/DocLinkAdminV1/{endpoint}
 */
export const buildDocLinkAdminPath = (endpoint) =>
  `${API_ROUTES.MANAGER_V1}/${API_VERSIONS.ADMIN_V1}/${API_VERSIONS.DOCLINK_ADMIN_V1}/${endpoint}`

/**
 * phAdminV1/HubMedAdminV1/{endpoint}
 */
export const buildHubMedAdminPath = (endpoint) =>
  `${API_ROUTES.MANAGER_V1}/${API_VERSIONS.ADMIN_V1}/${API_VERSIONS.HUBMED_ADMIN_V1}/${endpoint}`

/**
 * phAdminV1/HolaAdminV1/{endpoint}
 */
export const buildHolaAdminPath = (endpoint) =>
  `${API_ROUTES.MANAGER_V1}/${API_VERSIONS.ADMIN_V1}/${API_VERSIONS.HOLA_ADMIN_V1}/${endpoint}`

/** Hola Admin V1 resource names (no leading slash). */
export const HOLA_ADMIN_ENDPOINTS = {
  APPOINTMENT_OVERVIEW: 'AppointmentOverview',
  APPOINTMENT_REPORT: 'AppointmentReport',
}

/**
 * phAdminV1/MedmateAdminV1/{endpoint}
 */
export const buildMedmateAdminPath = (endpoint) =>
  `${API_ROUTES.MANAGER_V1}/${API_VERSIONS.ADMIN_V1}/${API_VERSIONS.MEDMATE_ADMIN_V1}/${endpoint}`

/** Medmate Admin V1 resource names (no leading slash). */
export const MEDMATE_ADMIN_ENDPOINTS = {
  APPOINTMENT_OVERVIEW: 'AppointmentOverview',
  SEARCH_CHEMISTS: 'SearchChemists',
  LINK_MEDMATE_CLINIC: 'LinkMedmateClinic',
  APPOINTMENT_REPORT: 'AppointmentReport',
}

/**
 * phAdminV1/HaloTextAdminV1/{endpoint}
 */
export const buildHaloTextAdminPath = (endpoint) =>
  `${API_ROUTES.MANAGER_V1}/${API_VERSIONS.ADMIN_V1}/${API_VERSIONS.HALOTEXT_ADMIN_V1}/${endpoint}`

/**
 * phAdminV1/CherubAdminV1/{endpoint}
 */
export const buildCherubAdminPath = (endpoint) =>
  `${API_ROUTES.MANAGER_V1}/${API_VERSIONS.ADMIN_V1}/${API_VERSIONS.CHERUB_ADMIN_V1}/${endpoint}`

export const CHERUB_ADMIN_ENDPOINTS = {
  OVERVIEW: 'Overview',
  GAPS: 'Gaps',
  TICKETS: 'Tickets',
  EVENTS: 'Events',
}

export const CHERUB_ADMIN_SSE_PATH = buildCherubAdminPath(CHERUB_ADMIN_ENDPOINTS.EVENTS)

export const buildCherubSessionMessagesPath = (sessionId) =>
  buildCherubAdminPath(`Session/${sessionId}/Messages`)

export const buildCherubSessionTicketsPath = (sessionId) =>
  buildCherubAdminPath(`Session/${sessionId}/Tickets`)

export const buildCherubTicketStatusPath = (ticketId) =>
  buildCherubAdminPath(`Tickets/${ticketId}/Status`)

/**
 * phAdminV1/ZephyrAdminV1/{endpoint}
 */
export const buildZephyrAdminPath = (endpoint) =>
  `${API_ROUTES.MANAGER_V1}/${API_VERSIONS.ADMIN_V1}/${API_VERSIONS.ZEPHYR_ADMIN_V1}/${endpoint}`

export const buildWorkflowPath = (endpoint) =>
  `${API_ROUTES.MANAGER_V1}/${API_VERSIONS.ADMIN_V1}/${API_VERSIONS.WORKFLOW_ADMIN_V1}/${endpoint}`

/** phAdminV1/DialpadV1/{endpoint} */
export const buildDialpadPath = (endpoint) =>
  `${API_ROUTES.MANAGER_V1}/${API_VERSIONS.ADMIN_V1}/DialpadV1/${endpoint}`

/** phAdminV1/CalendlyV1/{endpoint} */
export const buildCalendlyPath = (endpoint) =>
  `${API_ROUTES.MANAGER_V1}/${API_VERSIONS.ADMIN_V1}/CalendlyV1/${endpoint}`

export const CALENDLY_ENDPOINTS = {
  MANAGERS:          'managers',
  EVENTS:            'events',
  SYNC:              'sync',
  EVENT_TYPES:       'event-types',
  SEND_BOOKING_LINK: 'send-booking-link',
  AVAILABLE_TIMES:   'available-times',
}

export const ZEPHYR_ADMIN_ENDPOINTS = {
  INSURERS: 'Insurers',
  EMPLOYERS: 'Employers',
  OUTSTANDING_INVOICES: 'OutstandingInvoices',
  PENDING_SCRIPTS: 'PendingScripts',
  INVOICE_SCRIPTS: 'InvoiceScripts',
  CREATE_REMITTANCE: 'CreateRemittance',
  OVERVIEW: 'Overview',
  INVOICE_DETAILS: 'InvoiceDetails',
  CUSTOMER_SEARCH: 'CustomerSearch',
  CUSTOMER_DETAILS: 'CustomerDetails',
  FULL_SCRIPT_REPORT: 'FullScriptReport',
  UPDATE_CUSTOMER_COMMENT: 'UpdateCustomerComment',
  CLAIM_COMMENTS: 'ClaimComments',
  ADD_CLAIM_COMMENT: 'AddClaimComment',
  PAYMENT_DETAILS: 'PaymentDetails',
  RECONCILIATION_DETAILS: 'ReconciliationDetails',
  ZEPHYR_CHEMISTS: 'ZephyrChemists',
  UPDATE_INVOICE_COMMENT: 'InvoiceComment',
  CHEMIST_DETAILS: 'ChemistDetails',
  CHEMIST_NEXT_RECON: 'ChemistNextRecon',
  UPDATE_CHEMIST_BANK: 'ChemistBankDetails',
  OVERDUE_INVOICES: 'OverdueInvoices',
  WRITE_OFF: 'WriteOff',
  SEND_INVOICE: 'SendInvoice',
  SEND_ALL_OUTSTANDING: 'SendAllOutstanding',
  COMPOSE_EMAIL: 'ComposeEmail',
  CLAIM_INVOICES: 'ClaimInvoices',
  MERGE_INVOICES: 'MergeInvoices',
  MERGE_CUSTOMER_SEARCH: 'MergeCustomerSearch',
  MERGE_CUSTOMER_DETAILS: 'MergeCustomerDetails',
  MERGE_CANDIDATES: 'MergeCandidates',
  MERGE_CUSTOMERS: 'MergeCustomers',
  CLAIMS_OVERVIEW: 'ClaimsOverview',
  AUTO_RECON_PENDING: 'AutoRecon/Pending',
  AUTO_RECON_PROCESS: 'AutoRecon/Process',
  AUTO_RECON_PENDING_REVIEW: 'AutoRecon/PendingReview',
  AUTO_RECON_RESOLVE: 'AutoRecon/ResolveInvoice',
  AUTO_RECON_REMITTANCE: 'AutoRecon/Remittance',
  AUTO_RECON_PENDING_REMITTANCE: 'AutoRecon/PendingRemittance',
  RECENT_PAYMENTS: 'RecentPayments',
}

export const HALOTEXT_ADMIN_ENDPOINTS = {
  DEFAULT_TEMPLATES_LIST: 'GetList',
  DEFAULT_TEMPLATES_CREATE: 'Create',
  DEFAULT_TEMPLATES_UPDATE: 'Update',
  DEFAULT_TEMPLATES_SET_HIDDEN: 'SetHidden',
}

/** HubMed Admin V1 resource names (no leading slash). */
export const HUBMED_ADMIN_ENDPOINTS = {
  APPOINTMENT_OVERVIEW: 'AppointmentOverview',
  CONNECTED_CHEMISTS: 'ConnectedChemists',
  ONBOARDING_CHEMISTS: 'OnboardingChemists',
  ONBOARDING_DETAILS: 'OnboardingDetails',
  ONBOARDING_COMMENT: 'OnboardingComment',
  ONBOARDING_SET_HIDDEN: 'OnboardingSetHidden',
  PRACTITIONERS: 'Practitioners',
  PRACTITIONER_DETAILS: 'PractitionerDetails',
  UPDATE_PRACTITIONER_FLAGS: 'UpdatePractitionerFlags',
  SYNC_PRACTITIONERS: 'SyncPractitioners',
  DAILY_LOG: 'DailyLog',
  APPOINTMENT_REPORT: 'AppointmentReport',
}

/**
 * Admin HubMed DocLink pricing GET: .../DocLinkAdminV1/HubmedDoclinkPricing/:chemistID
 */
export const buildHubmedPricingPath = (chemistID) =>
  `${buildDocLinkAdminPath(ADMIN_ENDPOINTS.HUBMED_DOCLINK_PRICING)}/${chemistID}`

/** /managerv1/chemLinkV1/admin/{endpoint} */
export const buildChemLinkAdminPath = (endpoint) =>
  `${API_ROUTES.MANAGER_V1}/${API_VERSIONS.CHEM_LINK_V1}/admin/${endpoint}`

export const CHEM_LINK_ADMIN_ENDPOINTS = {
  GROUPS: 'Groups',
  GROUP_CHEMISTS: (id) => `Groups/${id}/Chemists`,
  GROUP_CHEMIST: (id, chemistId) => `Groups/${id}/Chemists/${chemistId}`,
  GROUP_CHEMIST_DELIVERY_MANAGER: (id, chemistId) => `Groups/${id}/Chemists/${chemistId}/DeliveryManager`,
  CHEMIST_SEARCH: 'ChemistSearch',
}

/** PUT .../LeaderboardCompetitions/:id */
export const buildLeaderboardCompetitionPath = (id) =>
  buildDocLinkAdminPath(`LeaderboardCompetitions/${id}`)

/** GET/POST/DELETE .../LeaderboardCompetitions/:id/Pharmacies */
export const buildLeaderboardCompetitionPharmaciesPath = (id) =>
  buildDocLinkAdminPath(`LeaderboardCompetitions/${id}/Pharmacies`)

/**
 * Build an auth/manager API path
 * @param {string} endpoint - The auth endpoint (e.g., AUTH_ENDPOINTS.MANAGER_LOGIN)
 * @returns {string} Full API path
 */
export const buildAuthPath = (endpoint) => {
  return `/${API_ROUTES.MANAGER_REGISTER}/${endpoint}`
}

// ============================================
// COMMONLY USED FULL PATHS (for convenience)
// ============================================

export const API_PATHS = {
  // Dashboard paths
  MAIN_DATA: buildDashboardPath(DASHBOARD_ENDPOINTS.MAIN_DATA),
  CHEMIST_DATA: buildDashboardPath(DASHBOARD_ENDPOINTS.CHEMIST_DATA),
  CHEMIST_SUBS: buildDashboardPath(DASHBOARD_ENDPOINTS.CHEMIST_SUBS),
  PING_APP_TEAM: buildDashboardPath(DASHBOARD_ENDPOINTS.PING_APP_TEAM),
  IS_PH_ADMIN: buildDashboardPath(DASHBOARD_ENDPOINTS.IS_PH_ADMIN),
  
  // Chart data paths
  DOCLINK_DASHBOARD: buildChartPath(CHART_ENDPOINTS.DOCLINK_DASHBOARD),
  QUDA_DASHBOARD: buildQudaPath(QUDA_ENDPOINTS.QUDA_DASHBOARD),
  ITEM_ORDERS: `${API_ROUTES.MANAGER_V1}/${API_VERSIONS.QUDA_DATA}/${QUDA_ENDPOINTS.ITEM_ORDERS}`,
  SUPPLIER_ORDERS: `${API_ROUTES.MANAGER_V1}/${API_VERSIONS.QUDA_DATA}/${QUDA_ENDPOINTS.SUPPLIER_ORDERS}`,
  SUPPLIER_PHARMACIES: `${API_ROUTES.MANAGER_V1}/${API_VERSIONS.QUDA_DATA}/${QUDA_ENDPOINTS.SUPPLIER_PHARMACIES}`,
  
  // Admin paths
  SETUP_HALOPAY: buildAdminPath(ADMIN_ENDPOINTS.SETUP_HALOPAY),
  CREATE_XERO_CONTACT: buildAdminPath(ADMIN_ENDPOINTS.CREATE_XERO_CONTACT),
  CHEMIST_LIST: buildAdminPath(ADMIN_ENDPOINTS.CHEMIST_LIST),
  ERROR_LOG: buildAdminPath(ADMIN_ENDPOINTS.ERROR_LOG),
  DEBUG_LOG: buildAdminPath(ADMIN_ENDPOINTS.DEBUG_LOG),
  STRIPE_PORTAL_LINK: buildAdminPath(ADMIN_ENDPOINTS.STRIPE_PORTAL_LINK),
  LOGS: buildAdminPath('Logs'),
  LOG_DATES: buildAdminPath('LogDates'),
  SEARCH_HALAXY_FOR_CHEMIST: buildDocLinkAdminPath(
    ADMIN_ENDPOINTS.SEARCH_HALAXY_FOR_CHEMIST
  ),
  LINK_HUBMED_CLINIC: buildDocLinkAdminPath(
    ADMIN_ENDPOINTS.LINK_HUBMED_CLINIC
  ),
  HUBMED_DOCLINK_PRICING: buildDocLinkAdminPath(
    ADMIN_ENDPOINTS.HUBMED_DOCLINK_PRICING
  ),
  DOCLINK_ADMIN_LEADERBOARD_COMPETITIONS: buildDocLinkAdminPath(ADMIN_ENDPOINTS.LEADERBOARD_COMPETITIONS),
  DOCLINK_ADMIN_LEADERBOARD: buildDocLinkAdminPath(ADMIN_ENDPOINTS.LEADERBOARD),

  // ChemLink admin (managerv1/chemLinkV1/admin/…)
  CHEM_LINK_ADMIN_GROUPS: buildChemLinkAdminPath(CHEM_LINK_ADMIN_ENDPOINTS.GROUPS),
  CHEM_LINK_ADMIN_CHEMIST_SEARCH: buildChemLinkAdminPath(CHEM_LINK_ADMIN_ENDPOINTS.CHEMIST_SEARCH),

  // Group managers (phAdminV1/GroupManagers/…)
  GROUP_MANAGERS_LIST: buildAdminPath('GroupManagers/List'),
  GROUP_MANAGERS_CREATE: buildAdminPath('GroupManagers/Create'),
  GROUP_MANAGERS_UPDATE: buildAdminPath('GroupManagers/Update'),
  GROUP_MANAGERS_ADD_CHEMISTS: buildAdminPath('GroupManagers/AddChemists'),
  GROUP_MANAGERS_REMOVE_CHEMISTS: buildAdminPath('GroupManagers/RemoveChemists'),
  GROUP_MANAGERS_ALL_CHEMISTS: buildAdminPath('GroupManagers/Chemists/List'),

  // PH Admin Functions (phAdminV1/adminOverviewV1/…)
  ADMIN_FUNCTIONS_OVERVIEW: buildAdminOverviewPath(ADMIN_OVERVIEW_ENDPOINTS.OVERVIEW),
  ADMIN_FUNCTIONS_QUDA: buildAdminOverviewPath(ADMIN_OVERVIEW_ENDPOINTS.QUDA),
  ADMIN_FUNCTIONS_DOCLINK: buildAdminOverviewPath(ADMIN_OVERVIEW_ENDPOINTS.DOCLINK),
  ADMIN_FUNCTIONS_DOCLINK_APPOINTMENTS: buildAdminOverviewPath(ADMIN_OVERVIEW_ENDPOINTS.DOCLINK_APPOINTMENTS),
  ADMIN_FUNCTIONS_SMS_BILLING: buildAdminOverviewPath(ADMIN_OVERVIEW_ENDPOINTS.SMS_BILLING),
  ADMIN_FUNCTIONS_HALOPAY: buildAdminOverviewPath(ADMIN_OVERVIEW_ENDPOINTS.HALOPAY),
  ADMIN_FUNCTIONS_HALOTEXT: buildAdminOverviewPath(ADMIN_OVERVIEW_ENDPOINTS.HALOTEXT),
  ADMIN_FUNCTIONS_MEDIDROP: buildAdminOverviewPath(ADMIN_OVERVIEW_ENDPOINTS.MEDIDROP),
  ADMIN_FUNCTIONS_WEBBIT: buildAdminOverviewPath(ADMIN_OVERVIEW_ENDPOINTS.WEBBIT),
  ADMIN_FUNCTIONS_CHEMIST_DETAIL: buildAdminOverviewPath(
    ADMIN_OVERVIEW_ENDPOINTS.CHEMIST_DETAIL
  ),
  ADMIN_CHEMIST_PAGE: buildAdminOverviewPath(ADMIN_OVERVIEW_ENDPOINTS.CHEMIST_PAGE),
  ADMIN_CHEMIST_NUMBERS: buildAdminOverviewPath('ChemistNumbers'),
  ADMIN_UPDATE_CHEMIST_GROUP: buildAdminOverviewPath('UpdateChemistGroup'),
  ADMIN_REPORTS: buildAdminOverviewPath(ADMIN_OVERVIEW_ENDPOINTS.REPORTS),
  ADMIN_TRIAL_HEALTH: buildAdminOverviewPath(ADMIN_OVERVIEW_ENDPOINTS.TRIAL_HEALTH),
  ADMIN_TERMINAL_ANALYTICS: buildAdminOverviewPath(ADMIN_OVERVIEW_ENDPOINTS.TERMINAL_ANALYTICS),

  // Conferences
  CONFERENCES:               buildAdminPath('ConferencesAdminV1'),
  CONFERENCE_BY_ID:          (id) => buildAdminPath(`ConferencesAdminV1/${id}`),
  CONFERENCE_ITEMS:          (id) => buildAdminPath(`ConferencesAdminV1/${id}/items`),
  CONFERENCE_CHECKLIST_ITEM: (id, itemId) => buildAdminPath(`ConferencesAdminV1/${id}/items/${itemId}`),
  ADMIN_ENGAGEMENT_REPORT:  buildAdminOverviewPath(ADMIN_OVERVIEW_ENDPOINTS.ENGAGEMENT_REPORT),
  ADMIN_FUNCTIONS_CHERUB: buildCherubAdminPath(CHERUB_ADMIN_ENDPOINTS.OVERVIEW),
  ADMIN_FUNCTIONS_CHERUB_GAPS: buildCherubAdminPath(CHERUB_ADMIN_ENDPOINTS.GAPS),
  ADMIN_FUNCTIONS_CHERUB_TICKETS: buildCherubAdminPath(CHERUB_ADMIN_ENDPOINTS.TICKETS),
  CHEMIST_QUDA: buildAdminOverviewPath(ADMIN_OVERVIEW_ENDPOINTS.CHEMIST_QUDA),
  CHEMIST_HALOPAY: buildAdminOverviewPath(ADMIN_OVERVIEW_ENDPOINTS.CHEMIST_HALOPAY),
  CHEMIST_HALOTEXT: buildAdminOverviewPath(ADMIN_OVERVIEW_ENDPOINTS.CHEMIST_HALOTEXT),
  CHEMIST_DOCLINK: buildAdminOverviewPath(ADMIN_OVERVIEW_ENDPOINTS.CHEMIST_DOCLINK),
  CHEMIST_DOCLINK_CONSULT_LOG: buildAdminOverviewPath('ConsultStatusLog'),
  CHEMIST_MEDIDROP: buildAdminOverviewPath(ADMIN_OVERVIEW_ENDPOINTS.CHEMIST_MEDIDROP),
  CHEMIST_MEDIDROP_DELIVERY_LOG: buildAdminOverviewPath('DeliveryStatusLog'),
  CHEMIST_WEBBIT: buildAdminOverviewPath(ADMIN_OVERVIEW_ENDPOINTS.CHEMIST_WEBBIT),
  CHEMIST_TERMINALS: buildAdminOverviewPath(ADMIN_OVERVIEW_ENDPOINTS.CHEMIST_TERMINALS),
  ADMIN_UPDATE_CHEMIST_FLAGS: buildAdminOverviewPath('UpdateChemistFlags'),
  ADMIN_UPDATE_CHEMIST_DETAILS: buildAdminOverviewPath('UpdateChemistDetails'),

  // Manager Functions (phAdminV1/adminOverviewV1/Manager* — no PHAdmin required)
  MANAGER_FUNCTIONS_OVERVIEW: buildManagerOverviewPath(MANAGER_OVERVIEW_ENDPOINTS.OVERVIEW),
  MANAGER_FUNCTIONS_QUDA: buildManagerOverviewPath(MANAGER_OVERVIEW_ENDPOINTS.QUDA),
  MANAGER_FUNCTIONS_DOCLINK: buildManagerOverviewPath(MANAGER_OVERVIEW_ENDPOINTS.DOCLINK),
  MANAGER_FUNCTIONS_HALOPAY: buildManagerOverviewPath(MANAGER_OVERVIEW_ENDPOINTS.HALOPAY),
  MANAGER_FUNCTIONS_HALOTEXT: buildManagerOverviewPath(MANAGER_OVERVIEW_ENDPOINTS.HALOTEXT),
  MANAGER_FUNCTIONS_MEDIDROP: buildManagerOverviewPath(MANAGER_OVERVIEW_ENDPOINTS.MEDIDROP),
  MANAGER_CHEMIST_PAGE: buildManagerOverviewPath(MANAGER_OVERVIEW_ENDPOINTS.CHEMIST_PAGE),
  MANAGER_CHEMIST_QUDA: buildManagerOverviewPath(MANAGER_OVERVIEW_ENDPOINTS.CHEMIST_QUDA),
  MANAGER_CHEMIST_HALOPAY: buildManagerOverviewPath(MANAGER_OVERVIEW_ENDPOINTS.CHEMIST_HALOPAY),
  MANAGER_CHEMIST_HALOTEXT: buildManagerOverviewPath(MANAGER_OVERVIEW_ENDPOINTS.CHEMIST_HALOTEXT),
  MANAGER_CHEMIST_DOCLINK: buildManagerOverviewPath(MANAGER_OVERVIEW_ENDPOINTS.CHEMIST_DOCLINK),
  MANAGER_CHEMIST_DOCLINK_CONSULT_LOG: buildManagerOverviewPath('ManagerConsultStatusLog'),
  MANAGER_CHEMIST_MEDIDROP: buildManagerOverviewPath(MANAGER_OVERVIEW_ENDPOINTS.CHEMIST_MEDIDROP),
  MANAGER_CHEMIST_MEDIDROP_DELIVERY_LOG: buildAdminOverviewPath('ManagerDeliveryStatusLog'),
  MANAGER_CHEMIST_LIST: buildManagerOverviewPath('ManagerChemistList'),

  // HubMed Admin (phAdminV1/HubMedAdminV1/…)
  HUBMED_ADMIN_APPOINTMENT_OVERVIEW: buildHubMedAdminPath(
    HUBMED_ADMIN_ENDPOINTS.APPOINTMENT_OVERVIEW
  ),
  HUBMED_ADMIN_CONNECTED_CHEMISTS: buildHubMedAdminPath(
    HUBMED_ADMIN_ENDPOINTS.CONNECTED_CHEMISTS
  ),
  HUBMED_ADMIN_ONBOARDING_CHEMISTS: buildHubMedAdminPath(
    HUBMED_ADMIN_ENDPOINTS.ONBOARDING_CHEMISTS
  ),
  HUBMED_ADMIN_ONBOARDING_DETAILS: buildHubMedAdminPath(
    HUBMED_ADMIN_ENDPOINTS.ONBOARDING_DETAILS
  ),
  HUBMED_ADMIN_ONBOARDING_COMMENT: buildHubMedAdminPath(
    HUBMED_ADMIN_ENDPOINTS.ONBOARDING_COMMENT
  ),
  HUBMED_ADMIN_ONBOARDING_SET_HIDDEN: buildHubMedAdminPath(
    HUBMED_ADMIN_ENDPOINTS.ONBOARDING_SET_HIDDEN
  ),
  HUBMED_ADMIN_PRACTITIONERS: buildHubMedAdminPath(
    HUBMED_ADMIN_ENDPOINTS.PRACTITIONERS
  ),
  HUBMED_ADMIN_PRACTITIONER_DETAILS: buildHubMedAdminPath(
    HUBMED_ADMIN_ENDPOINTS.PRACTITIONER_DETAILS
  ),
  HUBMED_ADMIN_UPDATE_PRACTITIONER_FLAGS: buildHubMedAdminPath(
    HUBMED_ADMIN_ENDPOINTS.UPDATE_PRACTITIONER_FLAGS
  ),
  HUBMED_ADMIN_SYNC_PRACTITIONERS: buildHubMedAdminPath(
    HUBMED_ADMIN_ENDPOINTS.SYNC_PRACTITIONERS
  ),
  HUBMED_ADMIN_DAILY_LOG: buildHubMedAdminPath(HUBMED_ADMIN_ENDPOINTS.DAILY_LOG),
  HUBMED_ADMIN_APPOINTMENT_REPORT: buildHubMedAdminPath(HUBMED_ADMIN_ENDPOINTS.APPOINTMENT_REPORT),

  // Hola Admin (phAdminV1/HolaAdminV1/…)
  HOLA_ADMIN_APPOINTMENT_OVERVIEW: buildHolaAdminPath(HOLA_ADMIN_ENDPOINTS.APPOINTMENT_OVERVIEW),
  HOLA_ADMIN_APPOINTMENT_REPORT: buildHolaAdminPath(HOLA_ADMIN_ENDPOINTS.APPOINTMENT_REPORT),

  // Medmate Admin (phAdminV1/MedmateAdminV1/…)
  MEDMATE_APPOINTMENT_OVERVIEW: buildMedmateAdminPath(MEDMATE_ADMIN_ENDPOINTS.APPOINTMENT_OVERVIEW),
  MEDMATE_SEARCH_CHEMISTS: buildMedmateAdminPath(MEDMATE_ADMIN_ENDPOINTS.SEARCH_CHEMISTS),
  MEDMATE_LINK_CLINIC: buildMedmateAdminPath(MEDMATE_ADMIN_ENDPOINTS.LINK_MEDMATE_CLINIC),
  MEDMATE_APPOINTMENT_REPORT: buildMedmateAdminPath(MEDMATE_ADMIN_ENDPOINTS.APPOINTMENT_REPORT),

  // Zephyr Admin (phAdminV1/ZephyrAdminV1/…)
  ZEPHYR_ADMIN_INSURERS: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.INSURERS),
  ZEPHYR_ADMIN_EMPLOYERS: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.EMPLOYERS),
  ZEPHYR_ADMIN_OUTSTANDING_INVOICES: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.OUTSTANDING_INVOICES),
  ZEPHYR_ADMIN_PENDING_SCRIPTS: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.PENDING_SCRIPTS),
  ZEPHYR_ADMIN_INVOICE_SCRIPTS: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.INVOICE_SCRIPTS),
  ZEPHYR_ADMIN_CREATE_REMITTANCE: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.CREATE_REMITTANCE),
  ZEPHYR_ADMIN_OVERVIEW: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.OVERVIEW),
  ZEPHYR_ADMIN_INVOICE_DETAILS: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.INVOICE_DETAILS),
  ZEPHYR_ADMIN_CUSTOMER_SEARCH: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.CUSTOMER_SEARCH),
  ZEPHYR_ADMIN_CUSTOMER_DETAILS: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.CUSTOMER_DETAILS),
  ZEPHYR_ADMIN_FULL_SCRIPT_REPORT: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.FULL_SCRIPT_REPORT),
  ZEPHYR_ADMIN_UPDATE_CUSTOMER_COMMENT: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.UPDATE_CUSTOMER_COMMENT),
  ZEPHYR_ADMIN_CLAIM_COMMENTS: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.CLAIM_COMMENTS),
  ZEPHYR_ADMIN_ADD_CLAIM_COMMENT: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.ADD_CLAIM_COMMENT),
  ZEPHYR_ADMIN_PAYMENT_DETAILS: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.PAYMENT_DETAILS),
  ZEPHYR_ADMIN_RECONCILIATION_DETAILS: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.RECONCILIATION_DETAILS),
  ZEPHYR_ADMIN_ZEPHYR_CHEMISTS: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.ZEPHYR_CHEMISTS),
  ZEPHYR_ADMIN_UPDATE_INVOICE_COMMENT: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.UPDATE_INVOICE_COMMENT),
  ZEPHYR_ADMIN_CHEMIST_DETAILS: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.CHEMIST_DETAILS),
  ZEPHYR_ADMIN_CHEMIST_NEXT_RECON: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.CHEMIST_NEXT_RECON),
  ZEPHYR_ADMIN_UPDATE_CHEMIST_BANK: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.UPDATE_CHEMIST_BANK),
  ZEPHYR_ADMIN_OVERDUE_INVOICES: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.OVERDUE_INVOICES),
  ZEPHYR_ADMIN_WRITE_OFF: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.WRITE_OFF),
  ZEPHYR_ADMIN_SEND_INVOICE: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.SEND_INVOICE),
  ZEPHYR_ADMIN_SEND_ALL_OUTSTANDING: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.SEND_ALL_OUTSTANDING),
  ZEPHYR_ADMIN_COMPOSE_EMAIL: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.COMPOSE_EMAIL),
  ZEPHYR_ADMIN_CLAIM_INVOICES: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.CLAIM_INVOICES),
  ZEPHYR_ADMIN_MERGE_INVOICES: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.MERGE_INVOICES),
  ZEPHYR_ADMIN_MERGE_CUSTOMER_SEARCH: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.MERGE_CUSTOMER_SEARCH),
  ZEPHYR_ADMIN_MERGE_CUSTOMER_DETAILS: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.MERGE_CUSTOMER_DETAILS),
  ZEPHYR_ADMIN_MERGE_CANDIDATES: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.MERGE_CANDIDATES),
  ZEPHYR_ADMIN_MERGE_CUSTOMERS: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.MERGE_CUSTOMERS),
  ZEPHYR_ADMIN_CLAIMS_OVERVIEW: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.CLAIMS_OVERVIEW),
  ZEPHYR_ADMIN_AUTO_RECON_PENDING: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.AUTO_RECON_PENDING),
  ZEPHYR_ADMIN_AUTO_RECON_PROCESS: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.AUTO_RECON_PROCESS),
  ZEPHYR_ADMIN_AUTO_RECON_PENDING_REVIEW: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.AUTO_RECON_PENDING_REVIEW),
  ZEPHYR_ADMIN_AUTO_RECON_RESOLVE: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.AUTO_RECON_RESOLVE),
  ZEPHYR_ADMIN_AUTO_RECON_REMITTANCE: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.AUTO_RECON_REMITTANCE),
  ZEPHYR_ADMIN_AUTO_RECON_PENDING_REMITTANCE: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.AUTO_RECON_PENDING_REMITTANCE),
  ZEPHYR_ADMIN_RECENT_PAYMENTS: buildZephyrAdminPath(ZEPHYR_ADMIN_ENDPOINTS.RECENT_PAYMENTS),

  // Zephyr Processing (phAdminV1/ZephyrAdminV1/Processing/…)
  ZEPHYR_PROCESSING_RECALCULATE: buildZephyrAdminPath('Processing/Recalculate'),
  ZEPHYR_PROCESSING_BATCH_SUMMARY: buildZephyrAdminPath('Processing/BatchSummary'),
  ZEPHYR_PROCESSING_PROCESS_ALL: buildZephyrAdminPath('Processing/ProcessAll'),
  ZEPHYR_PROCESSING_PRICING_REVIEW: buildZephyrAdminPath('Processing/PricingReview'),
  ZEPHYR_PROCESSING_PRICING_PREVIOUS: buildZephyrAdminPath('Processing/PricingReview/PreviousPrices'),
  ZEPHYR_PROCESSING_PRICING_SAVE: buildZephyrAdminPath('Processing/PricingReview/Save'),
  ZEPHYR_PROCESSING_COMCARE: buildZephyrAdminPath('Processing/Comcare'),
  ZEPHYR_PROCESSING_COMCARE_PROCESS: buildZephyrAdminPath('Processing/Comcare/Process'),
  ZEPHYR_PROCESSING_COMCARE_EMAIL: buildZephyrAdminPath('Processing/Comcare/SendEmail'),
  ZEPHYR_PROCESSING_COMCARE_SPLIT: buildZephyrAdminPath('Processing/Comcare/SplitBatch'),
  ZEPHYR_PROCESSING_MATCHED_BATCHES: buildZephyrAdminPath('Processing/MatchedBatches'),
  ZEPHYR_PROCESSING_GENERATE_INVOICES: buildZephyrAdminPath('Processing/GenerateInvoices'),

  // Zephyr Statements (phAdminV1/ZephyrAdminV1/Statements/…)
  ZEPHYR_STATEMENTS_CHECK_TODAY:  buildZephyrAdminPath('Statements/CheckToday'),
  ZEPHYR_STATEMENTS_GENERATE:     buildZephyrAdminPath('Statements/Generate'),
  ZEPHYR_STATEMENTS_HISTORY:      buildZephyrAdminPath('Statements/History'),
  ZEPHYR_STATEMENTS_ABA:          buildZephyrAdminPath('Statements/ABA'),
  ZEPHYR_STATEMENTS_CHEMIST:      buildZephyrAdminPath('Statements/Chemist'),
  ZEPHYR_STATEMENTS_DETAIL:       buildZephyrAdminPath('Statements/Detail'),

  // Zephyr Bank Reconciliation (phAdminV1/ZephyrAdminV1/BankRecon/…)
  ZEPHYR_RECON_SUMMARY:           buildZephyrAdminPath('BankRecon/Summary'),
  ZEPHYR_RECON_TRANSACTIONS:      buildZephyrAdminPath('BankRecon/Transactions'),
  ZEPHYR_RECON_PAYMENTS:          buildZephyrAdminPath('BankRecon/Payments'),
  ZEPHYR_RECON_SUGGESTIONS:       buildZephyrAdminPath('BankRecon/Suggestions'),
  ZEPHYR_RECON_UPLOAD:            buildZephyrAdminPath('BankRecon/Upload'),
  ZEPHYR_RECON_AUTO_MATCH:        buildZephyrAdminPath('BankRecon/AutoMatch'),
  ZEPHYR_RECON_MATCH:             buildZephyrAdminPath('BankRecon/Match'),
  ZEPHYR_RECON_UNMATCH:           buildZephyrAdminPath('BankRecon/Unmatch'),
  ZEPHYR_RECON_IGNORE_TRANS:      buildZephyrAdminPath('BankRecon/IgnoreTransaction'),
  ZEPHYR_RECON_UNIGNORE_TRANS:    buildZephyrAdminPath('BankRecon/UnignoreTransaction'),
  ZEPHYR_RECON_IGNORE_PAYMENT:    buildZephyrAdminPath('BankRecon/IgnorePayment'),
  ZEPHYR_RECON_UNIGNORE_PAYMENT:  buildZephyrAdminPath('BankRecon/UnignorePayment'),
  ZEPHYR_RECON_RECENTLY_MATCHED:  buildZephyrAdminPath('BankRecon/RecentlyMatched'),
  ZEPHYR_RECON_IMPORT_BATCHES:    buildZephyrAdminPath('BankRecon/ImportBatches'),
  ZEPHYR_RECON_ALIASES:           buildZephyrAdminPath('BankRecon/Aliases'),
  ZEPHYR_RECON_ALIAS:             buildZephyrAdminPath('BankRecon/Alias'),

  // HaloText Admin (phAdminV1/HaloTextAdminV1/…)
  HALOTEXT_ADMIN_DEFAULT_TEMPLATES_LIST: buildHaloTextAdminPath(HALOTEXT_ADMIN_ENDPOINTS.DEFAULT_TEMPLATES_LIST),
  HALOTEXT_ADMIN_DEFAULT_TEMPLATES_CREATE: buildHaloTextAdminPath(HALOTEXT_ADMIN_ENDPOINTS.DEFAULT_TEMPLATES_CREATE),
  HALOTEXT_ADMIN_DEFAULT_TEMPLATES_UPDATE: buildHaloTextAdminPath(HALOTEXT_ADMIN_ENDPOINTS.DEFAULT_TEMPLATES_UPDATE),
  HALOTEXT_ADMIN_DEFAULT_TEMPLATES_SET_HIDDEN: buildHaloTextAdminPath(HALOTEXT_ADMIN_ENDPOINTS.DEFAULT_TEMPLATES_SET_HIDDEN),

  // Dialpad (phAdminV1/DialpadV1/…)
  DIALPAD_DASHBOARD:        buildDialpadPath('dashboard'),
  DIALPAD_MATCH_PHONE:      buildDialpadPath('match-phone'),
  DIALPAD_CALLS:            buildDialpadPath('calls'),
  DIALPAD_CALLS_UNALLOCATED:buildDialpadPath('calls/unallocated'),
  DIALPAD_CALLS_RECENT:     buildDialpadPath('calls/recent'),
  DIALPAD_CALLS_RESCAN:     buildDialpadPath('calls/rescan'),
  DIALPAD_CALLS_ARCHIVE:    (id) => buildDialpadPath(`calls/${id}/archive`),
  DIALPAD_ADMIN_USERS:      buildDialpadPath('admin/users'),

  // Calendly (phAdminV1/CalendlyV1/…)
  CALENDLY_MANAGERS:          buildCalendlyPath(CALENDLY_ENDPOINTS.MANAGERS),
  CALENDLY_EVENTS:            buildCalendlyPath(CALENDLY_ENDPOINTS.EVENTS),
  CALENDLY_SYNC:              buildCalendlyPath(CALENDLY_ENDPOINTS.SYNC),
  CALENDLY_CONNECT_CHEMIST:   (eventId) => buildCalendlyPath(`events/${eventId}/connect-chemist`),
  CALENDLY_EVENT_TYPES:       buildCalendlyPath(CALENDLY_ENDPOINTS.EVENT_TYPES),
  CALENDLY_SEND_BOOKING_LINK: buildCalendlyPath(CALENDLY_ENDPOINTS.SEND_BOOKING_LINK),
  CALENDLY_AVAILABLE_TIMES:   buildCalendlyPath(CALENDLY_ENDPOINTS.AVAILABLE_TIMES),

  // Leaderboard competition management (phAdminV1/DocLinkAdminV1/LeaderboardCompetitions/…)
  LEADERBOARD_COMPETITIONS_ALL:         buildDocLinkAdminPath('LeaderboardCompetitions/All'),
  LEADERBOARD_COMPETITIONS_CREATE:      buildDocLinkAdminPath('LeaderboardCompetitions/Create'),
  LEADERBOARD_COMPETITIONS_SUGGEST_NAME: buildDocLinkAdminPath('LeaderboardCompetitions/SuggestName'),

  // Splashtop (phAdminV1/SplashtopV1/…)
  SPLASHTOP_SEND_SESSION: buildAdminPath('SplashtopV1/SendSupportSession'),
  SPLASHTOP_SEND_SUPPORT_APP_LINK: buildAdminPath('SplashtopV1/SendSupportAppLink'),

  // Global Headers (phAdminV1/GlobalHeadersV1/…) — desktop-app global header / warning banners
  GLOBAL_HEADERS: buildAdminPath('GlobalHeadersV1'),

  // Local Warnings (phAdminV1/LocalWarningsV1/…) — per-chemist warning banners
  LOCAL_WARNINGS: buildAdminPath('LocalWarningsV1'),

  // Auth paths
  MANAGER_LOGIN: buildAuthPath(AUTH_ENDPOINTS.MANAGER_LOGIN),
  VALIDATE_MANAGER: buildAuthPath(AUTH_ENDPOINTS.VALIDATE_MANAGER),
  LOGIN_WITH_GOOGLE: buildAuthPath(AUTH_ENDPOINTS.LOGIN_WITH_GOOGLE),
  CREATE_USER_WITH_GOOGLE: buildAuthPath(AUTH_ENDPOINTS.CREATE_USER_WITH_GOOGLE),
  LINK_GOOGLE_ACCOUNT: buildAuthPath(AUTH_ENDPOINTS.LINK_GOOGLE_ACCOUNT),
  CHECK_USER: buildAuthPath(AUTH_ENDPOINTS.CHECK_USER),
}

// ============================================
// USAGE EXAMPLES
// ============================================

/*
// Example 1: Using the full paths directly
import { API_PATHS } from '@/config/apiConstants'
const response = await phappsRequest(API_PATHS.DOCLINK_DASHBOARD, "GET")

// Example 2: Using builder functions for dynamic paths
import { buildDashboardPath, DASHBOARD_ENDPOINTS } from '@/config/apiConstants'
const path = buildDashboardPath(DASHBOARD_ENDPOINTS.CHEMIST_DATA, 123)
const response = await phappsRequest(path, "GET")

// Example 3: Using auth paths
import { API_PATHS } from '@/config/apiConstants'
const response = await apiRequest(API_PATHS.MANAGER_LOGIN, 'POST', { username, password })

// Example 4: Building custom paths
import { API_ROUTES, API_VERSIONS } from '@/config/apiConstants'
const customPath = `${API_ROUTES.MANAGER_V1}/${API_VERSIONS.DASHBOARD}/CustomEndpoint`
*/
